import { useLocation, useNavigate } from '@remix-run/react';
import { useTeamContext } from '~/context/TeamContext';
import { onAuthStateChanged, User } from 'firebase/auth';
import { RefObject, useEffect, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { auth } from '~/utils/auth/firebase-service';
import { getProfile, logoutWithBackend } from '~/utils/ServerRequest';
import logo from '../assets/CumulusLogo_Black.png';
import '../styles/header.css';
import { PopupModal } from './PopupModal';
import { useAuthStore } from '~/store/useAuthStore';
export const Header = ({
  pricingRef,
  disableModals = false,
  onLogoClick,
  modal: externalModal,
  switchModal: externalSwitchModal,
  priorityPage,
}: {
  pricingRef?: RefObject<HTMLDivElement>;
  disableModals?: boolean;
  onLogoClick?: () => void;
  modal?: 'login' | 'signup' | null;
  switchModal?: () => void;
  priorityPage?: 'login' | 'signup' | null;
}) => {
  const [modal, setModal] = useState<'login' | 'signup' | null | undefined>(
    priorityPage
  );
  const [stateUser, setStateUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [allowance, setAllowance] = useState<number>();
  const location = useLocation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const setUser = useAuthStore((state) => state.setUser);
  interface Profile {
    message: string;
    allowance: number;
    projects: [];
    signupComplete: boolean;
    subscription: string;
  }
  useEffect(() => {
    // Ensures user is authorised before initialising variables.
    console.log('[Header] Initial loading:', loading);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setStateUser(user);
          setModal(null);

          //fetching user alone is enough to stop the loading...
          setLoading(false);
          const prof = await getProfile();
          setProfile(prof);
          // Defensive: only check postSignupComplete if prof is an object
          if (
            prof &&
            typeof prof === 'object' &&
            'postSignupComplete' in prof &&
            !prof.postSignupComplete
          ) {
            navigate('/informationForm');
          }

          setAllowance(prof?.allowance);

          //zustand state
          setUser({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
          });
        } else {
          setStateUser(null);
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    console.log('[Header] Loading state changed:', loading);
  }, [loading]);
  const openSignUp = () => {
    if (!disableModals) {
      setModal('signup');
    } else if (disableModals && externalModal !== 'signup') {
      if (typeof externalSwitchModal === 'function') externalSwitchModal();
    }
  };
  const openLogin = () => {
    if (!disableModals) {
      setModal('login');
    } else if (disableModals && externalModal !== 'login') {
      if (typeof externalSwitchModal === 'function') externalSwitchModal();
    }
  };
  const closeAllAuthModals = () => {
    if (!disableModals) setModal(null);
    if (disableModals && onLogoClick) onLogoClick();
  };
  const switchModal = () => {
    setModal(modal === 'signup' ? 'login' : 'signup');
  };
  const handleLogout = () => {
    auth
      .signOut()
      .then(async () => {
        await logoutWithBackend();
        setStateUser(null);
        // @ts-ignore: Matomo tracking
        (window as any)._paq.push(['resetUserId']);
        // @ts-ignore: Matomo tracking
        (window as any)._paq.push(['appendToTrackingUrl', 'new_visit=1']);
        // @ts-ignore: Matomo tracking
        (window as any)._paq.push(['trackPageView']);
        // @ts-ignore: Matomo tracking
        (window as any)._paq.push(['appendToTrackingUrl', '']);
      })
      .catch((error) => {
        console.error('Error signing out:', error);
      });
  };
  let navigate = useNavigate();
  // Use TeamContext for team selection, always in sync with projects.create and projects.tsx
  const { eligibleTeams, selectedTeamId, setSelectedTeamId } = useTeamContext();
  console.log(
    '[Header] selectedTeamId:',
    selectedTeamId,
    'eligibleTeams:',
    eligibleTeams
  );
  // Sync URL with selected team when on /projects/create
  useEffect(() => {
    if (location.pathname.startsWith('/projects/create')) {
      const params = new URLSearchParams(window.location.search);
      if (selectedTeamId && params.get('team') !== selectedTeamId) {
        params.set('team', selectedTeamId);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId, location.pathname]);

  return (
    <header
      className={location.pathname === '/home' ? 'header' : 'uploads-header'}
    >
      <div id="headerContent">
        <div className="logoHeader">
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
            }}
            onClick={() => {
              closeAllAuthModals();
              if (!disableModals) navigate('/home');
            }}
            aria-label="Go to homepage"
          >
            <img
              src={logo}
              width={70}
              id="popupLogo"
              alt="CUMULUS"
              onError={(e) => {
                e.currentTarget.onerror = null; // Prevents infinite loop if the fallback also fails
              }}
            />
            <p className="headerButton">Cumulus</p>
          </button>
        </div>

        {/* Display header content for both home, uploads/projects, and project creation */}
        <div className="rightButtons">
          {/* Pricing button removed as requested */}

          {/* Show these buttons when user is not logged in */}
          {stateUser === null ? (
            <>
              <button
                className="headerButton"
                onClick={openLogin}
                disabled={loading}
                style={loading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                Log In
              </button>

              {/* Sign Up button - shown on both pages when not logged in */}
              <button
                id="signupButton"
                className="headerButton"
                onClick={openSignUp}
                disabled={loading}
                style={loading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                Sign Up <FaArrowRight />
              </button>
            </>
          ) : (
            <>
              {/* Show tokens and subscription info on uploads page */}
              {location.pathname === '/projects/list' && (
                <div className="alignItemsCenter">
                  <button
                    className="clearButton headerButtonText"
                    onClick={() => {}}
                  >
                    {allowance} tokens
                  </button>
                  <p className="headerButton">signed in as: </p>
                  <span style={{ display: 'flex', alignItems: 'left' }}>
                    <button className="clearButton" onClick={() => {}}>
                      {stateUser?.displayName}
                    </button>
                    <div
                      className={`headerButtonText tier-${profile?.subscription?.toLowerCase()}`}
                    >
                      {profile?.subscription}
                    </div>
                  </span>
                </div>
              )}

              {/* My Projects button - always visible when logged in */}
              <button
                className="headerButton"
                style={{ marginLeft: '16px' }}
                onClick={() => navigate('/projects/list')}
              >
                My Projects
              </button>

              {/* Profile button - always visible when logged in */}
              <button
                className="headerButton"
                style={{ marginLeft: '16px' }}
                onClick={() => navigate('/projects/profile')}
              >
                Profile
              </button>

              {/* Logout button - always visible when logged in */}
              <button
                className="headerButton logoutButton"
                style={{ marginLeft: '16px' }}
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          )}
        </div>

        {/* Only render PopupModal if not in modal context */}
        {!disableModals && modal && (
          <PopupModal
            mode={modal === 'signup' ? 'signUp' : 'login'}
            toggle={closeAllAuthModals}
            switchModal={switchModal}
          />
        )}
      </div>
    </header>
  );
};
